<template>
  <div class="map-container">
    <!-- 地图切换控件 -->
    <div class="map-layer-switcher">
      <a-select 
        :value="currentMapLayer" 
        placeholder="选择图层" 
        style="width: 120px;" 
        size="small"
        dropdownClassName="map-layer-select-dropdown" 
        :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        @change="handleMapLayerChange">
        <a-select-option value="layer1">高德矢量</a-select-option>
        <a-select-option value="layer2">高德影像</a-select-option>
        <a-select-option value="layer3">天地影像</a-select-option>
        <a-select-option value="layer4">必应地图</a-select-option>
        <a-select-option value="layer5">星图地图</a-select-option>
      </a-select>
    </div>
    <div id="leaflet-map" style="width: 100%; height: 700px"></div>

    <!-- 播放轨迹控制面板 -->
    <div class="trajectory-controls" v-if="path && path.length > 0 && path[0].point && path[0].point.length > 1">
      <!-- 控制按钮区域 -->
      <div class="control-section">
        <!-- 播放按钮 -->
        <button class="play-btn" @click="togglePlay" :disabled="isLoading">
          <img v-if="!isPlaying" :src="startIcon" alt="播放" class="btn-icon" />
          <img v-else :src="endIcon" alt="暂停" class="btn-icon" />
        </button>

        <!-- 速度选择按钮 -->
        <div class="speed-buttons">
          <button
            v-for="speed in speedOptions"
            :key="speed.value"
            :class="['speed-btn', { active: playSpeed === speed.value }]"
            @click="setPlaySpeed(speed.value)"
          >
            {{ speed.label }}
          </button>
        </div>

        <!-- 重置按钮 -->
        <button class="reset-btn" @click="resetPlay" :disabled="isPlaying">
          <img :src="resetIcon" alt="重置" class="btn-icon" />
        </button>

        <!-- 聚合切换按钮 -->
        <!-- <button 
          class="cluster-btn"
          @click="toggleClustering"
          :class="{ active: useClustering }"
          title="切换聚合显示"
        >
          <span class="cluster-icon">📍</span>
        </button> -->
      </div>

      <!-- 任务信息区域 -->
      <div class="task-info" v-if="path && path[0]">
        <div class="info-row">
          <div class="info-item">
            <div class="info-label">表册</div>
            <div class="info-value">{{ taskInfo.bookName || '--' }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">抄表员</div>
            <div class="info-value">{{ taskInfo.readerName || '--' }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">已抄户数</div>
            <div class="info-value clickable" @click="toggleReadList" v-if="taskInfo.alreadyReadCount > 0">
              {{ taskInfo.alreadyReadCount }}
            </div>
            <div class="info-value" v-else>--</div>
          </div>
          <div class="info-item">
            <div class="info-label">抄表率</div>
            <div class="info-value">{{ taskInfo.taskProgress || '--' }}%</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 已抄户列表 -->
    <div class="read-list-panel" v-if="readListVisible && (readList.length > 0 || noTrajectoryList.length > 0)">
      <div class="read-list-header">
        <span class="read-list-title">已抄户列表</span>
        <button class="close-btn" @click="closeReadList">×</button>
      </div>
      <!-- Tab 切换 -->
      <div class="read-list-tabs">
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'withTrajectory' }"
          @click="activeTab = 'withTrajectory'"
        >
          有轨迹 ({{ readList.length }})
        </div>
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'noTrajectory' }"
          @click="activeTab = 'noTrajectory'"
        >
          无轨迹 ({{ noTrajectoryList.length }})
        </div>
      </div>
      <div class="read-list-content">
        <!-- 有轨迹列表 -->
        <div v-if="activeTab === 'withTrajectory'">
          <div v-for="(house, index) in readList" :key="house.id || index" class="house-item" @click="selectHouse(house)">
            <div class="house-info">
              <div class="house-name">{{ house.userName || house.userCode || '户' }}</div>
              <div class="house-details">
                <span class="detail-item"> <strong>用户编码:</strong> {{ house.userCode || '--' }} </span>
                <span class="detail-item"> <strong>本期读数:</strong> {{ house.currentReading || '--' }} </span>
                <span class="detail-item"> <strong>本期用水量:</strong> {{ house.currentUsage || '--' }} m³ </span>
                <span class="detail-item">
                  <strong>抄表时间:</strong> {{ house.currentReadingTime ? formatDate(house.currentReadingTime) : '--' }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="readList.length === 0" class="empty-list">暂无有轨迹的已抄户数据</div>
        </div>
        <!-- 无轨迹列表 -->
        <div v-if="activeTab === 'noTrajectory'">
          <div v-for="(house, index) in noTrajectoryList" :key="house.id || index" class="house-item">
            <div class="house-info">
              <div class="house-name">{{ house.userName || house.userCode || '户' }}</div>
              <div class="house-details">
                <span class="detail-item"> <strong>用户编码:</strong> {{ house.userCode || '--' }} </span>
                <span class="detail-item"> <strong>本期读数:</strong> {{ house.currentReading || '--' }} </span>
                <span class="detail-item"> <strong>本期用水量:</strong> {{ house.currentUsage || '--' }} m³ </span>
                <span class="detail-item">
                  <strong>抄表时间:</strong> {{ house.currentReadingTime ? formatDate(house.currentReadingTime) : '--' }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="noTrajectoryList.length === 0" class="empty-list">暂无无轨迹的已抄户数据</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import startIcon from '@/assets/meterReading/start.svg'
import endIcon from '@/assets/meterReading/end.svg'
import resetIcon from '@/assets/meterReading/reset.svg'
import startPointIcon from '@/assets/myImg/1/start.png'
import endPointIcon from '@/assets/myImg/1/end.png'
import chinaProviders from '@/utils/chinatmsproviders'
import { LonLat } from '@/store/mutation-types'
import storage from 'store'

export default {
  props: {
    path: {
      type: Array,
      default: () => []
    },
    taskInfo: {
      type: Object,
      default: () => ({})
    },
    meterReadingTaskDetails: {
      type: Array,
      default: () => []
    }
  },
  created() {
    // 注册天地图插件
    chinaProviders(L)
  },
  data() {
    return {
      map: null,
      polyline: null,
      startMarker: null,
      endMarker: null,
      tileLayers: [], // 保存底图图层引用
      pointMarkers: [], // 保存轨迹点标记
      showPointMarkers: true, // 控制是否显示轨迹点标记
      startIcon,
      endIcon,
      resetIcon,
      startPointIcon,
      endPointIcon,

      // 播放轨迹相关状态
      isPlaying: false, // 是否正在播放
      isLoading: false, // 是否正在加载
      currentPlayIndex: 0, // 当前播放到的点索引
      totalPoints: 0, // 总点数
      playSpeed: 1, // 播放速度倍数
      playTimer: null, // 播放定时器
      toggleTimer: null, // 切换防抖定时器
      playMarkers: [], // 播放过程中的标记
      playPolyline: null, // 播放过程中的轨迹线
      playInterval: 50, // 播放间隔（毫秒）- 更小的间隔实现平滑动画
      currentPosition: null, // 当前位置
      targetPosition: null, // 目标位置
      animationProgress: 0, // 动画进度 0-1
      walkerMarker: null, // 行走者标记
      playState: 'stopped', // 播放状态: 'stopped', 'playing', 'paused'
      pausedAnimationProgress: 0, // 暂停时的动画进度
      speedOptions: [
        // 播放速度选项
        { label: '0.5x', value: 0.5 },
        { label: '1x', value: 1 },
        { label: '2x', value: 2 },
        { label: '4x', value: 4 }
      ],
      // 已抄户列表相关
      readListVisible: false,
      readList: [],
      noTrajectoryList: [], // 无轨迹的已抄户列表
      activeTab: 'withTrajectory', // 当前激活的tab: 'withTrajectory' | 'noTrajectory'
      selectedHouse: null,
      houseMarker: null,

      // 聚合相关
      markerClusterGroup: null, // 聚合组
      useClustering: true, // 是否启用聚合
      
      // 地图切换相关
      currentMapLayer: 'layer1', // 当前地图图层
      mapLayerConfigs: {
        layer1: {
          name: '高德矢量',
          url: 'https://wprd0{1-4}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&style=7&x={x}&y={y}&z={z}',
          projection: 'GCJ-02',
          maxZoom: 18
        },
        layer2: {
          name: '高德影像',
          url: 'https://webst01.is.autonavi.com/appmaptile?style=6&x={x}&y={y}&z={z}',
          projection: 'GCJ-02',
          maxZoom: 16
        },
        layer3: {
          name: '天地影像',
          url: 'https://t7.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=485cd3a62955d7a962a5c0d64d4949f2',
          projection: 'EPSG:3857',
          maxZoom: 18
        },
        layer4: {
          name: '必应地图',
          url: 'http://dynamic.t0.tiles.ditu.live.com/comp/ch/',
          urlEnd: '?it=G,VE,BX,L,LA&mkt=zh-cn,syr&n=z&og=111&ur=CN',
          useTileUrlFunction: true,
          projection: 'GCJ-02',
          maxZoom: 18
        },
        layer5: {
          name: '星图地图',
          url: 'https://api.open.geovisearth.com/pj/base/v1/img/',
          urlEnd: '?format=webp&tmsIds=w&token=131c96a153996bd9fb4ef1f9ffcb4ccf',
          useTileUrlFunction: true,
          projection: 'EPSG:3857',
          maxZoom: 18
        }
      }
    }
  },
  computed: {
    // 计算播放进度百分比
    playProgress() {
      if (this.totalPoints === 0) return 0
      return Math.round((this.currentPlayIndex / this.totalPoints) * 100)
    }
  },
  watch: {
    path: {
      handler(newPath) {
        if (this.map) {
          this.drawTrajectory(newPath)
        } else {
          // 如果地图还未初始化，等待一段时间后重试
          setTimeout(() => {
            if (this.map && newPath && newPath.length > 0) {
              this.drawTrajectory(newPath)
            }
          }, 500)
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap()
      setTimeout(() => {
        if (this.map) this.map.invalidateSize()
      }, 300)
    })
  },
  beforeDestroy() {
    // 清理播放定时器
    if (this.playTimer) {
      clearTimeout(this.playTimer)
      this.playTimer = null
    }

    // 清理切换防抖定时器
    if (this.toggleTimer) {
      clearTimeout(this.toggleTimer)
      this.toggleTimer = null
    }

    // 清理地图实例
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    initMap() {
      if (this.map) return
      // 设置默认中心点
      let center = [39.9042, 116.4074]
      let zoom = 8 // 降低初始缩放级别

      // 如果有轨迹数据，使用第一个点作为中心
      if (this.path?.[0]?.point?.[0]) {
        const [lng, lat] = this.path[0].point[0]
        center = [lat, lng]
        zoom = 10 // 有轨迹数据时使用稍大的缩放级别
      } else {
        // 尝试从存储中获取位置
        const storedLocation = storage.get(LonLat)
        if (storedLocation && Array.isArray(storedLocation) && storedLocation.length === 2) {
          center = [storedLocation[1], storedLocation[0]]
          zoom = 10
        }
      }

      this.map = L.map('leaflet-map', {
        zoomControl: true,
        minZoom: 1,
        maxZoom: 25,
        attributionControl: false
      }).setView(center, zoom)
      
      // 初始化默认地图图层
      this.createBaseLayer()

      // 初始化聚合组
      if (this.useClustering) {
        this.initMarkerClusterGroup()
      }

      if (this.path && this.path.length > 0) {
        this.drawTrajectory(this.path)
      }
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.map) this.map.invalidateSize()
        }, 300)
      })
    },
    drawTrajectory(pathArr) {
      if (!this.map || !pathArr || pathArr.length === 0) return
      const pathData = pathArr[0]
      const points = pathData.point || []
      const jsonVos = pathData.jsonVos || []
      if (points.length === 0) return

      // 清除聚合组
      this.clearMarkerClusterGroup()

      // 清除旧图层
      if (this.polyline) {
        this.map.removeLayer(this.polyline)
        this.polyline = null
      }
      if (this.startMarker) {
        this.map.removeLayer(this.startMarker)
        this.startMarker = null
      }
      if (this.endMarker) {
        this.map.removeLayer(this.endMarker)
        this.endMarker = null
      }
      // 清除轨迹点标记
      if (this.pointMarkers && this.pointMarkers.length > 0) {
        this.pointMarkers.forEach((marker) => {
          if (marker && this.map.hasLayer(marker)) {
            this.map.removeLayer(marker)
          }
        })
        this.pointMarkers = []
      }

      // 转换为 [lat, lng]
      const latlngs = points.map(([lng, lat]) => [lat, lng])
      console.log('轨迹latlngs:', latlngs)

      // 画轨迹 - 使用平滑效果
      this.polyline = L.polyline(latlngs, {
        color: '#12FF0D',
        weight: 3,
        smoothFactor: 1.0, // 增加平滑因子
        opacity: 0.8
      }).addTo(this.map)

      // 调整地图视图以显示整个轨迹
      if (latlngs.length > 1) {
        // 多个点时，使用fitBounds自动调整视图
        this.map.fitBounds(this.polyline.getBounds(), {
          padding: [20, 20], // 添加内边距
          maxZoom: 18 // 限制最大缩放级别，避免过度放大
        })
      } else if (latlngs.length === 1) {
        // 单个点时，设置合适的缩放级别
        this.map.setView(latlngs[0], 14)
      }

      // 轨迹点已经按时间排序，第一个点是起点，最后一个点是终点
      const startIndex = 0
      const endIndex = latlngs.length - 1

      // 起点 - 使用自定义图标
      const startIconConfig = L.icon({
        iconUrl: this.startPointIcon,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      })
      this.startMarker = L.marker(latlngs[startIndex], { icon: startIconConfig }).addTo(this.map)
      // this.startMarker.bindPopup(`
      //   <div style="min-width: 200px;">
      //     <div style="font-weight: bold; margin-bottom: 8px;">起点</div>
      //     <div style="margin-bottom: 4px;"><strong>用户:</strong> ${jsonVos[startIndex]?.userName || '--'}（${
      //   jsonVos[startIndex]?.userCode || '--'
      // }）</div>
      //     <div style="margin-bottom: 4px;"><strong>本期读数:</strong> ${
      //       jsonVos[startIndex]?.currentReading || '--'
      //     } m³</div>
      //     <div style="margin-bottom: 4px;"><strong>本期用水量:</strong> ${
      //       jsonVos[startIndex]?.currentUsage || '--'
      //     } m³</div>
      //     <div style="margin-bottom: 4px;"><strong>用水性质:</strong> ${this.getWaterUsageTypeText(
      //       jsonVos[startIndex]?.waterUsageType
      //     )}</div>
      //     <div style="margin-bottom: 4px;"><strong>抄表时间:</strong> ${
      //       jsonVos[startIndex]?.currentReadingTime ? this.formatDate(jsonVos[startIndex].currentReadingTime) : '--'
      //     }</div>
      //   </div>
      // `)

      // 终点 - 只有当有多个点时才显示终点标记
      if (latlngs.length > 1) {
        const endIconConfig = L.icon({
          iconUrl: this.endPointIcon,
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32]
        })
        this.endMarker = L.marker(latlngs[endIndex], { icon: endIconConfig }).addTo(this.map)
        // this.endMarker.bindPopup(`
        //   <div style="min-width: 200px;">
        //     <div style="font-weight: bold; margin-bottom: 8px;">终点</div>
        //     <div style="margin-bottom: 4px;"><strong>用户:</strong> ${jsonVos[endIndex]?.userName || '--'}（${
        //   jsonVos[endIndex]?.userCode || '--'
        // }）</div>
        //     <div style="margin-bottom: 4px;"><strong>本期读数:</strong> ${
        //       jsonVos[endIndex]?.currentReading || '--'
        //     } m³</div>
        //     <div style="margin-bottom: 4px;"><strong>本期用水量:</strong> ${
        //       jsonVos[endIndex]?.currentUsage || '--'
        //     } m³</div>
        //     <div style="margin-bottom: 4px;"><strong>用水性质:</strong> ${this.getWaterUsageTypeText(
        //       jsonVos[endIndex]?.waterUsageType
        //     )}</div>
        //     <div style="margin-bottom: 4px;"><strong>抄表时间:</strong> ${
        //       jsonVos[endIndex]?.currentReadingTime ? this.formatDate(jsonVos[endIndex].currentReadingTime) : '--'
        //     }</div>
        //   </div>
        // `)
      }

      // 为每个轨迹点添加圆点标记
      if (this.showPointMarkers) {
        this.addPointMarkers(latlngs, jsonVos)
      }

      this.$nextTick(() => {
        setTimeout(() => {
          if (this.map) this.map.invalidateSize()
        }, 300)
      })
    },

    // 重新绘制轨迹但不改变地图视图（用于重置播放）
    drawTrajectoryWithoutFit(pathArr) {
      if (!this.map || !pathArr || pathArr.length === 0) return
      const pathData = pathArr[0]
      const points = pathData.point || []
      const jsonVos = pathData.jsonVos || []
      if (points.length === 0) return

      // 清除旧图层
      if (this.polyline) {
        this.map.removeLayer(this.polyline)
        this.polyline = null
      }
      if (this.startMarker) {
        this.map.removeLayer(this.startMarker)
        this.startMarker = null
      }
      if (this.endMarker) {
        this.map.removeLayer(this.endMarker)
        this.endMarker = null
      }
      // 清除轨迹点标记
      if (this.pointMarkers && this.pointMarkers.length > 0) {
        this.pointMarkers.forEach((marker) => {
          if (marker && this.map.hasLayer(marker)) {
            this.map.removeLayer(marker)
          }
        })
        this.pointMarkers = []
      }

      // 转换为 [lat, lng]
      const latlngs = points.map(([lng, lat]) => [lat, lng])

      // 画轨迹 - 使用平滑效果，但不调整地图视图
      this.polyline = L.polyline(latlngs, {
        color: '#12FF0D',
        weight: 3,
        smoothFactor: 1.0,
        opacity: 0.8
      }).addTo(this.map)

      // 轨迹点已经按时间排序，第一个点是起点，最后一个点是终点
      const startIndex = 0
      const endIndex = latlngs.length - 1

      // 起点 - 使用自定义图标
      const startIconConfig = L.icon({
        iconUrl: this.startPointIcon,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      })
      this.startMarker = L.marker(latlngs[startIndex], { icon: startIconConfig }).addTo(this.map)
      // this.startMarker.bindPopup(`
      //   <div style="min-width: 200px;">
      //     <div style="font-weight: bold; margin-bottom: 8px;">起点</div>
      //     <div style="margin-bottom: 4px;"><strong>用户:</strong> ${jsonVos[startIndex]?.userName || '--'}（${
      //   jsonVos[startIndex]?.userCode || '--'
      // }）</div>
      //     <div style="margin-bottom: 4px;"><strong>本期读数:</strong> ${
      //       jsonVos[startIndex]?.currentReading || '--'
      //     } m³</div>
      //     <div style="margin-bottom: 4px;"><strong>本期用水量:</strong> ${
      //       jsonVos[startIndex]?.currentUsage || '--'
      //     } m³</div>
      //     <div style="margin-bottom: 4px;"><strong>用水性质:</strong> ${this.getWaterUsageTypeText(
      //       jsonVos[startIndex]?.waterUsageType
      //     )}</div>
      //     <div style="margin-bottom: 4px;"><strong>抄表时间:</strong> ${
      //       jsonVos[startIndex]?.currentReadingTime ? this.formatDate(jsonVos[startIndex].currentReadingTime) : '--'
      //     }</div>
      //   </div>
      // `)

      // 终点 - 只有当有多个点时才显示终点标记
      if (latlngs.length > 1) {
        const endIconConfig = L.icon({
          iconUrl: this.endPointIcon,
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32]
        })
        this.endMarker = L.marker(latlngs[endIndex], { icon: endIconConfig }).addTo(this.map)
        // this.endMarker.bindPopup(`
        //   <div style="min-width: 200px;">
        //     <div style="font-weight: bold; margin-bottom: 8px;">终点</div>
        //     <div style="margin-bottom: 4px;"><strong>用户:</strong> ${jsonVos[endIndex]?.userName || '--'}（${
        //   jsonVos[endIndex]?.userCode || '--'
        // }）</div>
        //     <div style="margin-bottom: 4px;"><strong>本期读数:</strong> ${
        //       jsonVos[endIndex]?.currentReading || '--'
        //     } m³</div>
        //     <div style="margin-bottom: 4px;"><strong>本期用水量:</strong> ${
        //       jsonVos[endIndex]?.currentUsage || '--'
        //     } m³</div>
        //     <div style="margin-bottom: 4px;"><strong>用水性质:</strong> ${this.getWaterUsageTypeText(
        //       jsonVos[endIndex]?.waterUsageType
        //     )}</div>
        //     <div style="margin-bottom: 4px;"><strong>抄表时间:</strong> ${
        //       jsonVos[endIndex]?.currentReadingTime ? this.formatDate(jsonVos[endIndex].currentReadingTime) : '--'
        //     }</div>
        //   </div>
        // `)
      }

      // 为每个轨迹点添加圆点标记
      if (this.showPointMarkers) {
        this.addPointMarkers(latlngs, jsonVos)
      }

      this.$nextTick(() => {
        setTimeout(() => {
          if (this.map) this.map.invalidateSize()
        }, 300)
      })
    },

    // 为每个轨迹点添加圆点标记（只显示isReading为true的抄表点）
    addPointMarkers(latlngs, jsonVos) {
      if (!latlngs || latlngs.length === 0) return

      // 统计isReading为true的点数量
      const isReadingPoints = jsonVos.filter((point) => point.isReading === true)
      console.log(`总点数: ${latlngs.length}, isReading为true的点数: ${isReadingPoints.length}`)

      // 创建圆点标记样式
      const pointIcon = L.divIcon({
        className: 'trajectory-point-marker',
        html: '<div style="width: 12px; height: 12px; background-color: #FF6B6B; border: 2px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      })

      let markerCount = 0
      const markers = [] // 用于聚合的标记数组

      // 为每个点创建标记（包含所有点，不跳过起点和终点）
      latlngs.forEach((latlng, index) => {
        // 检查该点是否为抄表状态（isReading为true）
        const pointInfo = jsonVos[index] || {}
        if (pointInfo.isReading !== true) {
          console.log(`跳过非抄表点: index=${index}, isReading=${pointInfo.isReading}`)
          return // 跳过非抄表状态的点
        }

        markerCount++
        console.log(`创建标记: index=${index}, 用户=${pointInfo.userName}, 累计标记数=${markerCount}`)

        const marker = L.marker(latlng, {
          icon: pointIcon,
          interactive: true // 确保标记可以交互
        })

        // 绑定点击事件，显示用户信息
        marker.bindPopup(`
           <div style="min-width: 200px;">             
             <div style="margin-bottom: 4px;"><strong>用户:</strong> ${pointInfo.userName || '--'}（${
          pointInfo.userCode || '--'
        }）</div>
             <div style="margin-bottom: 4px;"><strong>本期读数:</strong> ${pointInfo.currentReading || '--'} m³</div>
             <div style="margin-bottom: 4px;"><strong>本期用水量:</strong> ${pointInfo.currentUsage || '--'} m³</div>
             <div style="margin-bottom: 4px;"><strong>用水性质:</strong> ${this.getWaterUsageTypeText(
               pointInfo.waterUsageType
             )}</div>
             <div style="margin-bottom: 4px;"><strong>抄表时间:</strong> ${
               pointInfo.currentReadingTime ? this.formatDate(pointInfo.currentReadingTime) : '--'
             }</div>
           </div>
         `)

        // 保存标记引用到临时数组
        markers.push(marker)
      })

      // 如果启用聚合且有标记，则添加到聚合组
      if (this.useClustering && markers.length > 0) {
        this.addMarkersToCluster(markers)
        // 聚合模式下，直接使用新的标记数组，避免重复
        this.pointMarkers = markers
      } else if (markers.length > 0) {
        // 如果不使用聚合，直接添加到地图
        markers.forEach((marker) => {
          marker.addTo(this.map)
        })
        this.pointMarkers = markers
      }

      console.log(`最终创建的标记数量: ${markerCount}, pointMarkers数组长度: ${this.pointMarkers.length}`)
    },

    // 获取用水性质文本
    getWaterUsageTypeText(type) {
      const typeMap = {
        1: '居民用水',
        2: '非居民用水',
        3: '特种用水',
        4: '工业用水',
        5: '混合用水'
      }
      return typeMap[type] || '--'
    },

    // 格式化日期
    // formatDate(date) {
    //   if (!date) return '--'
    //   const d = new Date(date)
    //   return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
    //     2,
    //     '0'
    //   )} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    // },

    // 切换轨迹点标记显示状态
    togglePointMarkers() {
      this.showPointMarkers = !this.showPointMarkers

      if (this.showPointMarkers) {
        // 重新绘制轨迹以显示点标记
        if (this.path && this.path.length > 0) {
          this.drawTrajectory(this.path)
        }
      } else {
        // 清除所有点标记
        if (this.pointMarkers && this.pointMarkers.length > 0) {
          this.pointMarkers.forEach((marker) => {
            if (marker && this.map.hasLayer(marker)) {
              this.map.removeLayer(marker)
            }
          })
          this.pointMarkers = []
        }
      }

      console.log(`轨迹点标记显示状态: ${this.showPointMarkers ? '开启' : '关闭'}`)
    },

    // 清理图层方法（供外部调用）
    removeLayer(layerName) {
      console.log(`清理图层: ${layerName}`)
      // 清除轨迹线
      if (this.polyline) {
        this.map.removeLayer(this.polyline)
        this.polyline = null
      }
      // 清除起点终点标记
      if (this.startMarker) {
        this.map.removeLayer(this.startMarker)
        this.startMarker = null
      }
      if (this.endMarker) {
        this.map.removeLayer(this.endMarker)
        this.endMarker = null
      }
      // 清除轨迹点标记
      if (this.pointMarkers && this.pointMarkers.length > 0) {
        this.pointMarkers.forEach((marker) => {
          if (marker && this.map.hasLayer(marker)) {
            this.map.removeLayer(marker)
          }
        })
        this.pointMarkers = []
      }
      // 清除聚合组
      this.clearMarkerClusterGroup()
    },

    // ========== 播放轨迹相关方法 ==========

    // 切换播放状态
    togglePlay() {
      // 防抖处理，避免连续快速点击
      if (this.toggleTimer) {
        clearTimeout(this.toggleTimer)
      }

      this.toggleTimer = setTimeout(() => {
        if (this.isPlaying) {
          this.pausePlay()
        } else {
          this.startPlay()
        }
      }, 100) // 100ms防抖
    },

    // 重新开始播放（完全重置）
    restartPlay() {
      console.log('重新开始播放')

      // 强制重置所有播放状态
      this.isPlaying = false
      this.currentPlayIndex = 0
      this.totalPoints = 0
      this.animationProgress = 0
      this.currentPosition = null
      this.targetPosition = null

      // 清除所有播放相关的图层和定时器
      if (this.playTimer) {
        clearTimeout(this.playTimer)
        this.playTimer = null
      }

      this.clearPlayLayers()
      this.clearWalker()

      // 开始新的播放
      this.startPlay()
    },

    // 开始播放轨迹
    startPlay() {
      if (!this.path || this.path.length === 0 || !this.path[0].point || this.path[0].point.length <= 1) {
        this.$message && this.$message.warning('没有可播放的轨迹数据')
        return
      }

      // 如果已经在播放，先停止
      if (this.isPlaying) {
        this.pausePlay()
      }

      this.isLoading = true

      // 初始化总点数
      if (this.totalPoints === 0) {
        this.totalPoints = this.path[0].point.length
      }

      console.log('当前播放状态:', this.playState, '当前索引:', this.currentPlayIndex)

      // 根据播放状态决定行为
      if (this.playState === 'stopped' || this.currentPlayIndex >= this.totalPoints - 1) {
        console.log('从头开始播放轨迹')
        // 从头开始播放
        this.currentPlayIndex = 0
        this.animationProgress = 0
        this.currentPosition = null
        this.targetPosition = null

        // 清除原有的轨迹显示
        this.clearPlayLayers()

        // 隐藏原有的轨迹点标记
        this.hideOriginalMarkers()

        // 初始化行走者位置
        this.initWalker()
      } else if (this.playState === 'paused') {
        console.log('继续播放轨迹，当前索引:', this.currentPlayIndex, '当前位置:', this.currentPosition)
        console.log('恢复动画进度:', this.pausedAnimationProgress)

        // 从暂停位置继续播放 - 保持所有状态
        this.animationProgress = this.pausedAnimationProgress

        // 确保行走者存在且位置正确
        if (!this.walkerMarker && this.currentPosition) {
          // 从当前位置重新创建行走者
          const walkerIcon = L.divIcon({
            className: 'walker-marker',
            html: `
              <div class="walker-content">
                <div class="walker-avatar">🚶</div>
                <div class="walker-shadow"></div>
              </div>
            `,
            iconSize: [32, 32],
            iconAnchor: [16, 32]
          })
          this.walkerMarker = L.marker(this.currentPosition, { icon: walkerIcon }).addTo(this.map)
          console.log('从暂停位置重新创建行走者:', this.currentPosition)
        } else if (!this.walkerMarker) {
          // 如果没有保存的位置，重新初始化
          this.initWalker()
        }
      } else {
        console.log('重新初始化播放')
        // 其他情况，重新初始化
        this.currentPlayIndex = 0
        this.animationProgress = 0
        this.currentPosition = null
        this.targetPosition = null
        this.clearPlayLayers()
        this.hideOriginalMarkers()
        this.initWalker()
      }

      this.isPlaying = true
      this.playState = 'playing'

      // 开始播放（从当前位置继续）
      this.startSmoothPlay()

      this.isLoading = false
    },

    // 暂停播放 - 保存精确的暂停状态
    pausePlay() {
      console.log('暂停播放，保存当前状态')
      console.log('暂停时状态:', {
        currentPlayIndex: this.currentPlayIndex,
        animationProgress: this.animationProgress,
        currentPosition: this.currentPosition,
        targetPosition: this.targetPosition
      })

      this.isPlaying = false
      this.playState = 'paused'
      this.pausedAnimationProgress = this.animationProgress

      if (this.playTimer) {
        clearTimeout(this.playTimer)
        this.playTimer = null
      }
      // 暂停时不清除任何位置信息和行走者标记
    },

    // 停止播放 - 改进版本，保持地图状态
    stopPlay() {
      console.log('停止播放')

      // 保存当前地图状态
      const currentZoom = this.map ? this.map.getZoom() : null
      const currentCenter = this.map ? this.map.getCenter() : null

      this.isPlaying = false
      this.playState = 'stopped'
      this.currentPlayIndex = 0
      this.animationProgress = 0
      this.pausedAnimationProgress = 0
      this.totalPoints = 0 // 重置总点数，表示需要重新初始化
      this.currentPosition = null
      this.targetPosition = null

      if (this.playTimer) {
        clearTimeout(this.playTimer)
        this.playTimer = null
      }

      // 清除播放图层
      this.clearPlayLayers()

      // 清除行走者标记
      this.clearWalker()

      // 恢复原有轨迹显示
      this.showOriginalMarkers()

      // 重新绘制完整轨迹，但不改变地图视图
      if (this.path && this.path.length > 0) {
        this.drawTrajectoryWithoutFit(this.path)

        // 恢复之前的地图状态
        if (this.map && currentZoom !== null && currentCenter !== null) {
          this.map.setView(currentCenter, currentZoom, {
            animate: false
          })
        }
      }
    },

    // 重置播放 - 保持当前地图层级
    resetPlay() {
      console.log('重置播放状态')

      // 保存当前地图状态
      const currentZoom = this.map ? this.map.getZoom() : null
      const currentCenter = this.map ? this.map.getCenter() : null

      // 完全重置播放状态
      this.isPlaying = false
      this.playState = 'stopped'
      this.currentPlayIndex = 0
      this.animationProgress = 0
      this.pausedAnimationProgress = 0
      this.totalPoints = 0
      this.currentPosition = null
      this.targetPosition = null

      // 清除定时器
      if (this.playTimer) {
        clearTimeout(this.playTimer)
        this.playTimer = null
      }

      // 清除播放图层
      this.clearPlayLayers()

      // 清除行走者标记
      this.clearWalker()

      // 恢复原有轨迹显示
      this.showOriginalMarkers()

      // 重新绘制完整轨迹，但不改变地图视图
      if (this.path && this.path.length > 0) {
        this.drawTrajectoryWithoutFit(this.path)
      }

      // 恢复地图状态，避免改变层级
      if (this.map && currentZoom !== null && currentCenter !== null) {
        this.map.setView(currentCenter, currentZoom, {
          animate: false // 不使用动画，立即恢复
        })
      }
    },

    // 初始化行走者
    initWalker() {
      if (!this.path || this.path.length === 0 || !this.path[0].point || this.path[0].point.length === 0) {
        return
      }

      // 先清除之前的行走者标记，防止重复创建
      this.clearWalker()

      const points = this.path[0].point
      // 确保索引在有效范围内
      const startIndex = Math.max(0, Math.min(this.currentPlayIndex, points.length - 1))
      const startPoint = points[startIndex]

      if (!startPoint || startPoint.length < 2) {
        console.error('Invalid start point for walker:', startPoint)
        return
      }

      const [lng, lat] = startPoint

      // 创建行走者标记
      const walkerIcon = L.divIcon({
        className: 'walker-marker',
        html: `
          <div class="walker-content">
            <div class="walker-avatar">🚶</div>
            <div class="walker-shadow"></div>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 32]
      })

      this.walkerMarker = L.marker([lat, lng], { icon: walkerIcon }).addTo(this.map)
      this.currentPosition = [lat, lng]
      this.animationProgress = 0

      console.log(`行走者初始化在索引 ${startIndex}，位置 [${lat}, ${lng}]`)
    },

    // 开始平滑播放
    startSmoothPlay() {
      if (!this.isPlaying || this.currentPlayIndex >= this.totalPoints - 1) {
        this.isPlaying = false
        return
      }

      const points = this.path[0].point

      // 确保当前位置正确设置
      if (!this.currentPosition || !Array.isArray(this.currentPosition)) {
        console.log('重新设置当前位置，索引:', this.currentPlayIndex)
        const currentPoint = points[this.currentPlayIndex]
        const [lng, lat] = currentPoint
        this.currentPosition = [lat, lng]

        // 更新行走者位置
        if (this.walkerMarker) {
          this.walkerMarker.setLatLng([lat, lng])
        }
      } else {
        console.log('保持当前位置:', this.currentPosition)
        // 确保行走者位置与当前位置一致
        if (this.walkerMarker) {
          this.walkerMarker.setLatLng(this.currentPosition)
        }
      }

      // 检查是否有下一个点
      if (this.currentPlayIndex + 1 >= this.totalPoints) {
        console.log('已到达最后一个点')
        this.isPlaying = false
        this.playState = 'stopped'
        return
      }

      // 设置下一个目标点
      const nextPoint = points[this.currentPlayIndex + 1]
      const [lng, lat] = nextPoint

      this.targetPosition = [lat, lng]

      // 只有在目标位置改变或播放状态为停止时才重置动画进度
      if (this.playState !== 'paused' && (this.animationProgress >= 1 || this.animationProgress < 0)) {
        this.animationProgress = 0
      }

      console.log('开始动画，从:', this.currentPosition, '到:', this.targetPosition, '进度:', this.animationProgress)

      // 开始动画
      this.animateToNextPoint()
    },

    // 动画到下一个点
    animateToNextPoint() {
      if (!this.isPlaying || !this.currentPosition || !this.targetPosition) {
        return
      }

      // 使用缓动函数实现平滑动画
      this.animationProgress += 0.03 * this.playSpeed // 调整动画速度，增加步进

      if (this.animationProgress >= 1) {
        this.animationProgress = 1
        // 动画完成，移动到下一个点
        this.onAnimationComplete()
        return
      }

      // 计算当前位置（线性插值）
      const currentLat =
        this.currentPosition[0] + (this.targetPosition[0] - this.currentPosition[0]) * this.animationProgress
      const currentLng =
        this.currentPosition[1] + (this.targetPosition[1] - this.currentPosition[1]) * this.animationProgress

      // 更新行走者位置
      if (this.walkerMarker) {
        this.walkerMarker.setLatLng([currentLat, currentLng])
      }

      // 添加轨迹线
      this.addPointToPlayTrajectory([currentLat, currentLng])

      // 继续动画
      this.playTimer = setTimeout(() => {
        this.animateToNextPoint()
      }, this.playInterval)
    },

    // 动画完成回调
    onAnimationComplete() {
      if (!this.isPlaying) return

      // 更新当前位置
      this.currentPosition = [...this.targetPosition]

      // 添加到达点的标记（只有isReading为true的点才添加标记）
      const jsonVos = this.path[0].jsonVos || []
      const pointInfo = jsonVos[this.currentPlayIndex + 1] || {}

      // 只有isReading为true的点才添加播放标记
      if (pointInfo.isReading === true) {
        // 计算当前是第几个isReading为true的点
        const readingPointIndex = jsonVos
          .slice(0, this.currentPlayIndex + 1)
          .filter((item) => item.isReading === true).length
        this.addPlayMarker(this.targetPosition, pointInfo, readingPointIndex)
      }

      // 移动到下一个点
      this.currentPlayIndex++

      if (this.currentPlayIndex < this.totalPoints - 1) {
        // 继续播放下一个点
        setTimeout(() => {
          this.startSmoothPlay()
        }, Math.max(100, 300 / this.playSpeed)) // 根据播放速度调整停顿时间
      } else {
        // 播放完成
        this.isPlaying = false
        this.playState = 'stopped'
        console.log('轨迹播放完成')
        // this.$message.success('轨迹播放完成')
      }
    },

    // 添加点到播放轨迹线
    addPointToPlayTrajectory(latlng) {
      if (!this.playPolyline) {
        // 创建播放轨迹线
        this.playPolyline = L.polyline([latlng], {
          color: '#FF6B6B',
          weight: 4,
          opacity: 0.8,
          smoothFactor: 1.0
        }).addTo(this.map)
      } else {
        // 添加点到现有轨迹线
        const currentLatLngs = this.playPolyline.getLatLngs()
        currentLatLngs.push(latlng)
        this.playPolyline.setLatLngs(currentLatLngs)
      }
    },

    // 添加播放标记
    addPlayMarker(latlng, pointInfo, index) {
      // 创建播放标记图标
      const playIcon = L.divIcon({
        className: 'play-trajectory-marker',
        html: `
          <div class="play-marker-content">
            <div class="play-marker-dot"></div>
          </div>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      })

      const marker = L.marker(latlng, { icon: playIcon }).addTo(this.map)

      // 绑定弹窗信息
      // <div style="font-weight: bold; margin-bottom: 8px; color: #FF6B6B;">播放点 ${index + 1}</div>
      marker.bindPopup(`
        <div style="min-width: 200px;">          
          <div style="margin-bottom: 4px;"><strong>用户:</strong> ${pointInfo.userName || '--'}（${
        pointInfo.userCode || '--'
      }）</div>
          <div style="margin-bottom: 4px;"><strong>本期读数:</strong> ${pointInfo.currentReading || '--'} m³</div>
          <div style="margin-bottom: 4px;"><strong>本期用水量:</strong> ${pointInfo.currentUsage || '--'} m³</div>
          <div style="margin-bottom: 4px;"><strong>用水性质:</strong> ${this.getWaterUsageTypeText(
            pointInfo.waterUsageType
          )}</div>
          <div style="margin-bottom: 4px;"><strong>抄表时间:</strong> ${
            pointInfo.currentReadingTime ? this.formatDate(pointInfo.currentReadingTime) : '--'
          }</div>
        </div>
      `)

      this.playMarkers.push(marker)
    },

    // 清除播放图层
    clearPlayLayers() {
      // 清除播放轨迹线
      if (this.playPolyline) {
        this.map.removeLayer(this.playPolyline)
        this.playPolyline = null
      }

      // 清除播放标记
      if (this.playMarkers && this.playMarkers.length > 0) {
        this.playMarkers.forEach((marker) => {
          if (marker && this.map.hasLayer(marker)) {
            this.map.removeLayer(marker)
          }
        })
        this.playMarkers = []
      }
    },

    // 清除行走者
    clearWalker() {
      if (this.walkerMarker) {
        this.map.removeLayer(this.walkerMarker)
        this.walkerMarker = null
      }
      this.currentPosition = null
      this.targetPosition = null
      this.animationProgress = 0
    },

    // 隐藏原有标记
    hideOriginalMarkers() {
      if (this.pointMarkers && this.pointMarkers.length > 0) {
        this.pointMarkers.forEach((marker) => {
          if (marker && this.map.hasLayer(marker)) {
            this.map.removeLayer(marker)
          }
        })
      }
    },

    // 显示原有标记
    showOriginalMarkers() {
      if (this.showPointMarkers && this.path && this.path.length > 0) {
        const points = this.path[0].point || []
        const jsonVos = this.path[0].jsonVos || []
        const latlngs = points.map(([lng, lat]) => [lat, lng])
        this.addPointMarkers(latlngs, jsonVos)
      }
    },

    // 设置播放速度
    setPlaySpeed(speed) {
      this.playSpeed = speed
      this.updatePlaySpeed()
    },

    // 更新播放速度
    updatePlaySpeed() {
      // 播放速度改变时，不需要重新设置定时器
      // 因为动画是基于animationProgress的增量控制的
      // 播放速度会在下一次animateToNextPoint调用时生效
      console.log(`播放速度更新为: ${this.playSpeed}x`)
    },

    // 切换已抄户列表显示
    toggleReadList() {
      if (this.readListVisible) {
        this.closeReadList()
        return
      }

      // 从轨迹数据中筛选出有轨迹的已抄户（isReading: true）
      let withTrajectoryList = []
      if (this.path && this.path.length > 0 && this.path[0].jsonVos) {
        const jsonVos = this.path[0].jsonVos || []
        withTrajectoryList = jsonVos.filter((item) => item.isReading === true)
      }
      
      // 从 meterReadingTaskDetails 中获取无轨迹的已抄户
      const noTrajectoryList = (this.meterReadingTaskDetails || []).filter((item) => {
        // 判断是否为已抄户（可以根据实际字段判断，比如有 currentReading 或 currentReadingTime）
        return item.currentReading != null || item.currentReadingTime != null
      })

      this.readList = withTrajectoryList
      this.noTrajectoryList = noTrajectoryList

      if (this.readList.length === 0 && this.noTrajectoryList.length === 0) {
        this.$message.info('暂无已抄户数据')
        return
      }

      // 根据数据情况设置默认 tab
      if (this.readList.length > 0) {
        this.activeTab = 'withTrajectory'
      } else if (this.noTrajectoryList.length > 0) {
        this.activeTab = 'noTrajectory'
      }

      this.readListVisible = true
    },

    // 关闭已抄户列表
    closeReadList() {
      this.readListVisible = false
      this.readList = []
      this.noTrajectoryList = []
      this.selectedHouse = null
      this.activeTab = 'withTrajectory'
    },

    // 选择某户并定位到地图
    selectHouse(house) {
      this.selectedHouse = house

      // 从轨迹数据中找到对应的坐标
      if (!this.path || this.path.length === 0 || !this.path[0].point || !this.path[0].jsonVos) {
        this.$message.warning('没有轨迹数据')
        return
      }

      const jsonVos = this.path[0].jsonVos
      const points = this.path[0].point

      // 找到该户在jsonVos中的索引
      const houseIndex = jsonVos.findIndex((item) => item.userCode === house.userCode && item.isReading === true)

      if (houseIndex === -1 || houseIndex >= points.length) {
        this.$message.warning('找不到该户的位置信息')
        return
      }

      // 获取对应的坐标点 [lng, lat]
      const point = points[houseIndex]
      if (!point || point.length < 2) {
        this.$message.warning('该户位置信息不完整')
        return
      }

      const lng = point[0]
      const lat = point[1]

      // 验证坐标是否有效
      if (isNaN(lat) || isNaN(lng)) {
        this.$message.error('坐标数据无效')
        return
      }

      // 验证坐标范围
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        this.$message.error('坐标超出有效范围')
        return
      }

      // 设置地图中心点（不改变缩放级别）
      if (this.map) {
        try {
          // 使用 panTo 设置地图中心点，保持当前缩放级别
          this.map.panTo([lat, lng], {
            animate: true,
            duration: 1.0
          })
        } catch (error) {
          this.$message.error('设置地图中心点失败')
        }
      } else {
        this.$message.error('地图未初始化')
      }

      // 添加高亮标记
      this.addHouseMarker(house, lat, lng)
    },

    // 添加户标记到地图
    addHouseMarker(house, lat, lng) {
      // 清除之前的户标记
      if (this.houseMarker) {
        this.map.removeLayer(this.houseMarker)
      }

      // 创建户标记（使用自定义图标）
      const houseIcon = L.divIcon({
        className: 'house-marker',
        html: `
          <div class="house-marker-content">
            <div class="house-marker-icon">📍</div>
          </div>
        `,
        iconSize: [30, 30],
        iconAnchor: [15, 30]
      })

      this.houseMarker = L.marker([lat, lng], { icon: houseIcon })
        .addTo(this.map)
        .bindPopup(
          `
          <div class="house-popup">
            <h4>${house.userName || house.userCode || '户'}</h4>
            <p><strong>用户编码:</strong> ${house.userCode || '--'}</p>
            <p><strong>本期读数:</strong> ${house.currentReading || '--'}</p>
            <p><strong>本期用水量:</strong> ${house.currentUsage || '--'} m³</p>
            <p><strong>抄表时间:</strong> ${
              house.currentReadingTime ? this.formatDate(house.currentReadingTime) : '--'
            }</p>
            <p><strong>用水性质:</strong> ${this.getWaterUsageTypeText(house.waterUsageType)}</p>
          </div>
        `
        )
        .openPopup()
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '--'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN')
    },
    
    // 创建底图图层
    createBaseLayer() {
      if (!this.map) {
        console.warn('地图未初始化，无法创建底图图层')
        return
      }
      
      console.log('创建底图图层，当前图层:', this.currentMapLayer)
      
      // 清除旧图层 - 更彻底的方式
      if (this.tileLayers && this.tileLayers.length > 0) {
        console.log('清除旧图层，数量:', this.tileLayers.length)
        this.tileLayers.forEach((layer, index) => {
          if (layer) {
            try {
              if (this.map.hasLayer(layer)) {
                console.log(`移除图层 ${index}`)
                this.map.removeLayer(layer)
              }
              // 清理图层资源
              if (layer.remove) {
                layer.remove()
              }
            } catch (error) {
              console.warn(`移除图层 ${index} 时出错:`, error)
            }
          }
        })
        this.tileLayers = []
      }
      
      // 额外检查：清除地图上所有未管理的瓦片图层（防止有遗漏的底图图层）
      // 注意：polyline 是 L.Polyline，不是 L.TileLayer，所以不需要检查
      // 由于 tileLayers 已经被清空，所以这里清除所有瓦片图层（除了轨迹相关的）
      const layersToRemove = []
      this.map.eachLayer((layer) => {
        // 只清除瓦片图层，且不是轨迹相关的图层
        if (layer instanceof L.TileLayer) {
          // 检查是否是轨迹相关的图层（这些不应该被清除）
          const isTrajectoryRelated = layer === this.polyline || 
                                      layer === this.playPolyline ||
                                      (this.pointMarkers && this.pointMarkers.includes(layer)) ||
                                      (this.playMarkers && this.playMarkers.includes(layer))
          if (!isTrajectoryRelated) {
            layersToRemove.push(layer)
          }
        }
      })
      
      // 批量移除图层
      if (layersToRemove.length > 0) {
        console.log('发现额外的瓦片图层，准备移除:', layersToRemove.length, '个')
        layersToRemove.forEach((layer) => {
          try {
            this.map.removeLayer(layer)
            console.log('已移除额外图层:', layer)
          } catch (error) {
            console.warn('移除额外图层时出错:', error)
          }
        })
      }
      
      const config = this.mapLayerConfigs[this.currentMapLayer]
      if (!config) {
        console.error('找不到图层配置:', this.currentMapLayer)
        return
      }
      
      console.log('使用配置创建图层:', config.name, config.url)
      
      // 处理特殊URL（必应地图和星图地图）
      if (config.useTileUrlFunction) {
        // 必应地图和星图地图需要特殊处理，使用自定义的 getTileUrl 方法
        const layerName = config.name // 保存到局部变量，避免闭包问题
        const layerUrl = config.url
        const layerUrlEnd = config.urlEnd
        const layerMaxZoom = config.maxZoom
        const TileLayerClass = L.TileLayer.extend({
          getTileUrl: function(coords) {
            const z = coords.z
            const x = coords.x
            const y = coords.y
            
            if (layerName === '必应地图') {
              let quadKey = ''
              let tileX = x
              let tileY = y
              for (let i = 0; i < z; i++) {
                quadKey = ((tileX & 1) + 2 * (tileY & 1)).toString() + quadKey
                tileX >>= 1
                tileY >>= 1
              }
              return layerUrl + quadKey + layerUrlEnd
            } else if (layerName === '星图地图') {
              const quadKey = z + '/' + x + '/' + y
              return layerUrl + quadKey + layerUrlEnd
            }
            return ''
          }
        })
        
        const tileLayer = new TileLayerClass('', {
          maxZoom: layerMaxZoom,
          attribution: '',
          errorTileUrl: '' // 防止加载失败时显示错误图片
        })
        tileLayer.addTo(this.map)
        this.tileLayers = [tileLayer]
        console.log('特殊瓦片图层已添加（必应/星图），图层数量:', this.tileLayers.length, '图层对象:', tileLayer)
      } else {
        // 普通瓦片图层
        // 检查 URL 是否包含 {1-4}（高德地图的随机服务器选择）
        if (config.url.includes('{1-4}')) {
          // 使用自定义的 getTileUrl 方法来处理随机服务器选择
          const layerUrl = config.url // 保存 URL 到局部变量，避免闭包问题
          const layerMaxZoom = config.maxZoom // 保存 maxZoom 到局部变量
          const TileLayerClass = L.TileLayer.extend({
            getTileUrl: function(coords) {
              const z = coords.z
              const x = coords.x
              const y = coords.y
              // 随机选择 1-4 之间的服务器
              const server = Math.floor(Math.random() * 4) + 1
              return layerUrl
                .replace('{1-4}', server)
                .replace('{x}', x)
                .replace('{y}', y)
                .replace('{z}', z)
            }
          })
          const tileLayer = new TileLayerClass('', {
            maxZoom: layerMaxZoom,
            attribution: '',
            errorTileUrl: '' // 防止加载失败时显示错误图片
          })
          tileLayer.addTo(this.map)
          this.tileLayers = [tileLayer]
          console.log('自定义瓦片图层已添加（包含{1-4}），图层数量:', this.tileLayers.length, '图层对象:', tileLayer)
        } else {
          // 普通瓦片图层（高德影像、天地影像等）
          console.log('创建普通瓦片图层:', config.url)
          const tileLayer = L.tileLayer(config.url, {
            maxZoom: config.maxZoom,
            attribution: '',
            errorTileUrl: '' // 防止加载失败时显示错误图片
          })
          tileLayer.addTo(this.map)
          this.tileLayers = [tileLayer]
          console.log('普通瓦片图层已添加，图层数量:', this.tileLayers.length, '图层对象:', tileLayer)
        }
      }
    },
    
    // 处理地图图层切换
    handleMapLayerChange(value) {
      console.log('切换地图图层 - 新值:', value, '当前值:', this.currentMapLayer)
      
      // 检查是否真的需要切换（新值和当前值相同，且已有图层）
      if (this.currentMapLayer === value && this.tileLayers.length > 0) {
        console.log('图层相同且已存在，无需切换')
        return
      }
      
      // 更新 currentMapLayer
      this.currentMapLayer = value
      
      console.log('开始创建新图层，配置:', this.mapLayerConfigs[value])
      
      // 创建新图层
      this.createBaseLayer()
      
      // 强制刷新地图，确保新图层显示
      this.$nextTick(() => {
        if (this.map) {
          // 刷新地图尺寸
          this.map.invalidateSize()
          // 重新加载当前视图的瓦片
          if (this.tileLayers && this.tileLayers.length > 0) {
            this.tileLayers.forEach((layer) => {
              if (layer && layer.redraw) {
                layer.redraw()
              }
            })
          }
          console.log('地图已刷新，新图层数量:', this.tileLayers.length)
          // 验证图层是否真的在地图上
          const layersOnMap = []
          this.map.eachLayer((layer) => {
            if (layer instanceof L.TileLayer) {
              layersOnMap.push(layer)
            }
          })
          console.log('地图上的瓦片图层数量:', layersOnMap.length, '图层列表:', layersOnMap)
        }
      })
    },

    // ========== 聚合相关方法 ==========

    // 初始化聚合组
    initMarkerClusterGroup() {
      if (this.markerClusterGroup) {
        this.map.removeLayer(this.markerClusterGroup)
      }

      this.markerClusterGroup = L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 80, // 增加聚合半径
        spiderfyOnMaxZoom: true, // 最大缩放时展开
        showCoverageOnHover: true, // 悬停时显示覆盖范围
        zoomToBoundsOnClick: true, // 点击时缩放到边界
        disableClusteringAtZoom: 16, // 在16级缩放时禁用聚合
        iconCreateFunction: this.createClusterIcon,
        removeOutsideVisibleBounds: true, // 移除可见区域外的标记
        animate: true, // 启用动画
        animateAddingMarkers: true // 添加标记时启用动画
      })

      this.markerClusterGroup.addTo(this.map)
    },

    // 创建聚合图标
    createClusterIcon(cluster) {
      const childCount = cluster.getChildCount()
      console.log(`创建聚合图标，子标记数量: ${childCount}`)

      let className = 'marker-cluster-small'

      if (childCount >= 100) {
        className = 'marker-cluster-large'
      } else if (childCount >= 10) {
        className = 'marker-cluster-medium'
      }

      return L.divIcon({
        html: `<div><span>${childCount}</span></div>`,
        className: `marker-cluster ${className}`,
        iconSize: L.point(40, 40)
      })
    },

    // 添加标记到聚合组
    addMarkersToCluster(markers) {
      if (!this.markerClusterGroup) {
        this.initMarkerClusterGroup()
      } else {
        // 清除旧的标记，避免重复
        this.markerClusterGroup.clearLayers()
      }

      console.log(`添加 ${markers.length} 个标记到聚合组`)
      this.markerClusterGroup.addLayers(markers)

      // 添加事件监听器来调试聚合
      this.markerClusterGroup.on('clusteradd', (e) => {
        console.log('聚合组添加了标记:', e.layer.getChildCount())
      })
    },

    // 清除聚合组
    clearMarkerClusterGroup() {
      if (this.markerClusterGroup) {
        this.markerClusterGroup.clearLayers()
        this.map.removeLayer(this.markerClusterGroup)
        this.markerClusterGroup = null
      }
    },

    // 切换聚合功能
    toggleClustering() {
      this.useClustering = !this.useClustering

      if (this.useClustering) {
        // 启用聚合，重新绘制标记
        if (this.path && this.path.length > 0) {
          this.clearMarkerClusterGroup()
          this.pointMarkers = []
          const points = this.path[0].point || []
          const jsonVos = this.path[0].jsonVos || []
          const latlngs = points.map(([lng, lat]) => [lat, lng])
          this.addPointMarkers(latlngs, jsonVos)
        }
      } else {
        // 禁用聚合，清除聚合组，直接显示标记
        this.clearMarkerClusterGroup()
        if (this.pointMarkers.length > 0) {
          this.pointMarkers.forEach((marker) => {
            if (!this.map.hasLayer(marker)) {
              marker.addTo(this.map)
            }
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: 700px;
  
  .map-layer-switcher {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 2000;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    padding: 4px;
  }
}

/* 确保下拉框的 z-index 也足够高 */
.map-layer-switcher /deep/ .ant-select-dropdown {
  z-index: 2001 !important;
}

#leaflet-map {
  width: 100%;
  height: 700px;
}

/* 播放轨迹控制面板样式 */
.trajectory-controls {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  padding: 12px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  min-width: 500px;
  max-width: 600px;
  width: 500px;
  color: white;
  box-sizing: border-box;
}

.control-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.play-btn,
.reset-btn,
.cluster-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.play-btn:hover,
.reset-btn:hover,
.cluster-btn:hover {
  transform: scale(1.05);
}

.play-btn:disabled,
.reset-btn:disabled {
  /* background: #8c8c8c; */
  cursor: not-allowed;
  transform: none;
}

/* 聚合按钮样式 */
.cluster-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.cluster-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.cluster-btn.active {
  background: rgba(24, 144, 255, 0.8);
  border-color: #1890ff;
  color: white;
}

.cluster-icon {
  font-size: 18px;
  line-height: 1;
}

.btn-icon {
  width: 20px;
  height: 20px;
}

.speed-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.speed-btn {
  padding: 6px 12px;
  border: none;
  background: transparent;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.speed-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.speed-btn.active {
  color: #1890ff;
}
/* 
.task-info {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 12px;
} */

.info-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 16px;
  width: 100%;
  min-width: 0;
}

.info-item {
  text-align: center;
  min-width: 0;
  overflow: hidden;
}

.info-label {
  font-size: 12px;
  color: #b3b3b3;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: white;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.info-value.clickable {
  color: #40a9ff;
  cursor: pointer;
  text-decoration: underline;
}

.info-value.clickable:hover {
  color: #69c0ff;
}

/* 已抄户列表面板样式 */
.read-list-panel {
  position: absolute;
  top: 150px;
  right: 20px;
  width: 250px;
  max-height: 500px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 8px;
  color: white;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.read-list-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  
  .tab-item {
    flex: 1;
    padding: 10px 12px;
    text-align: center;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.3s;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    font-size: 13px;
    
    &:hover {
      color: #40a9ff;
      background: rgba(255, 255, 255, 0.1);
    }
    
    &.active {
      color: #40a9ff;
      border-bottom-color: #40a9ff;
      background: rgba(255, 255, 255, 0.05);
    }
  }
}

.read-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.read-list-title {
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.read-list-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  box-sizing: border-box;
  min-height: 0;
}

/* 自定义滚动条样式 */
.read-list-content::-webkit-scrollbar {
  width: 6px;
}

.read-list-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.read-list-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.read-list-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Firefox 滚动条样式 */
.read-list-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
}

.house-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  margin-bottom: 8px;
}

.empty-list {
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.house-item:hover {
  border-color: #40a9ff;
  background: rgba(64, 169, 255, 0.2);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(64, 169, 255, 0.3);
}

.house-info {
  flex: 1;
}

.house-name {
  font-size: 14px;
  font-weight: 600;
  color: white;
  margin-bottom: 6px;
}

.house-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.detail-item strong {
  color: white;
  margin-right: 4px;
}

.house-actions {
  margin-left: 16px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

/* 户标记样式 */
:deep(.house-marker) {
  cursor: pointer;
}

:deep(.house-marker-content) {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #ff4d4f;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

:deep(.house-marker-icon) {
  font-size: 18px;
  line-height: 1;
}

:deep(.house-popup) {
  min-width: 200px;
}

:deep(.house-popup h4) {
  margin: 0 0 8px 0;
  color: #1890ff;
  font-size: 16px;
}

:deep(.house-popup p) {
  margin: 4px 0;
  font-size: 14px;
  color: #333;
}

/* 行走者标记样式 */
:deep(.walker-marker) {
  cursor: pointer;
  transition: transform 0.1s ease;
}

:deep(.walker-content) {
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.walker-avatar) {
  font-size: 24px;
  animation: walk 0.8s ease-in-out infinite alternate;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

:deep(.walker-shadow) {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  animation: shadowPulse 0.8s ease-in-out infinite alternate;
}

/* 行走动画 */
@keyframes walk {
  0% {
    transform: translateY(0px) rotate(-2deg);
  }
  100% {
    transform: translateY(-2px) rotate(2deg);
  }
}

/* 阴影动画 */
@keyframes shadowPulse {
  0% {
    transform: translateX(-50%) scale(1);
    opacity: 0.3;
  }
  100% {
    transform: translateX(-50%) scale(1.1);
    opacity: 0.5;
  }
}

/* 播放标记样式 */
:deep(.play-trajectory-marker) {
  cursor: pointer;
  transition: transform 0.3s ease;
}

:deep(.play-trajectory-marker:hover) {
  transform: scale(1.2);
}

:deep(.play-marker-content) {
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.play-marker-dot) {
  width: 16px;
  height: 16px;
  background: #ff6b6b;
  border: 3px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  animation: pulse 2s infinite;
}

/* 数字样式已移除 */

/* 脉冲动画 */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 107, 107, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0);
  }
}

/* 轨迹点标记样式 */
:deep(.trajectory-point-marker) {
  cursor: pointer;
  transition: transform 0.2s ease;
}

:deep(.trajectory-point-marker:hover) {
  transform: scale(1.2);
}

/* 弹窗样式优化 */
:deep(.leaflet-popup-content-wrapper) {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.leaflet-popup-content) {
  margin: 12px;
  font-size: 14px;
  line-height: 1.5;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .trajectory-controls {
    top: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
    padding: 12px;
  }

  .control-section {
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }

  .speed-buttons {
    order: -1;
    width: 100%;
    justify-content: center;
  }

  .info-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .play-btn,
  .reset-btn {
    width: 36px;
    height: 36px;
  }

  .btn-icon {
    width: 18px;
    height: 18px;
  }
}

/* 聚合标记样式 */
:deep(.marker-cluster) {
  background-clip: padding-box;
  border-radius: 20px;
}

:deep(.marker-cluster div) {
  width: 30px;
  height: 30px;
  margin-left: 5px;
  margin-top: 5px;
  text-align: center;
  border-radius: 15px;
  font: 12px 'Helvetica Neue', Arial, Helvetica, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
}

:deep(.marker-cluster-small) {
  background-color: rgba(181, 226, 140, 0.6);
}

:deep(.marker-cluster-small div) {
  background-color: rgba(110, 204, 57, 0.8);
}

:deep(.marker-cluster-medium) {
  background-color: rgba(241, 211, 87, 0.6);
}

:deep(.marker-cluster-medium div) {
  background-color: rgba(240, 194, 12, 0.8);
}

:deep(.marker-cluster-large) {
  background-color: rgba(253, 156, 115, 0.6);
}

:deep(.marker-cluster-large div) {
  background-color: rgba(241, 128, 23, 0.8);
}

/* 聚合标记悬停效果 */
:deep(.marker-cluster:hover) {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}
</style>
